// Words and numbers the Reports screen shows. Codes come from the database as they are (Polaris decodes them the same way).
export const n = (v: unknown) => { const x = Number(v ?? 0); return Number.isFinite(x) ? x : 0 }

/** $1,234.56, or $1,235 when `whole`. Negative numbers keep the sign in front of the $. */
export function money(v: unknown, whole = false) {
  const x = n(v)
  const s = Math.abs(x).toLocaleString('en-US', whole ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return (x < 0 ? '-$' : '$') + s
}
/** Tiles: whole dollars under $100k, $1.2M above. */
export function moneyTile(v: unknown) {
  const x = n(v)
  if (Math.abs(x) >= 1_000_000) return (x < 0 ? '-$' : '$') + (Math.abs(x) / 1_000_000).toFixed(2).replace(/\.?0+$/, '') + 'M'
  return money(x, true)
}
export const pct = (v: unknown, digits = 0) => `${n(v).toFixed(digits)}%`
export const int = (v: unknown) => n(v).toLocaleString('en-US')

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DOWL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** "October 2026" from "2026-10". */
export function monthName(m: string) { const [y, mm] = m.split('-').map(Number); return `${MONTHS[mm - 1]} ${y}` }
export function monthShort(m: string) { const [y, mm] = m.split('-').map(Number); return `${SHORT[mm - 1]} ${y}` }
export function addMonths(m: string, d: number) { const [y, mm] = m.split('-').map(Number); const t = new Date(Date.UTC(y, mm - 1 + d, 1)); return t.toISOString().slice(0, 7) }
/** "8 Oct" from a date or timestamp string. */
export function dayShort(v: unknown) {
  if (!v) return ''
  const s = String(v); const d = new Date(s.length === 10 ? s + 'T12:00:00' : s)
  if (Number.isNaN(d.getTime())) return s
  return `${d.getDate()} ${SHORT[d.getMonth()]}`
}
export function dayLong(v: unknown) {
  if (!v) return ''
  const s = String(v); const d = new Date(s.length === 10 ? s + 'T12:00:00' : s)
  if (Number.isNaN(d.getTime())) return s
  return `${DOWL[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}
export function dayDow(v: unknown) {
  if (!v) return ''
  const s = String(v); const d = new Date(s.length === 10 ? s + 'T12:00:00' : s)
  if (Number.isNaN(d.getTime())) return s
  return `${DOW[d.getDay()]} ${d.getDate()} ${SHORT[d.getMonth()]}`
}
/** "10:12" from a New York timestamp the views hand back ("2026-10-08T10:12:33"). */
export function timeOf(v: unknown) {
  if (!v) return ''
  const m = String(v).match(/T(\d{2}):(\d{2})/)
  return m ? `${m[1]}:${m[2]}` : ''
}
/** "Today 4:31am" / "Yesterday 4:31am" / "8 Oct 4:31am" for a UTC timestamp. */
export function whenShort(iso: unknown, today: string) {
  if (!iso) return 'never'
  const d = new Date(String(iso))
  if (Number.isNaN(d.getTime())) return String(iso)
  const ny = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
  const t = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }).format(d).toLowerCase().replace(' ', '')
  const yest = new Date(today + 'T12:00:00Z'); yest.setUTCDate(yest.getUTCDate() - 1)
  const label = ny === today ? 'Today' : ny === yest.toISOString().slice(0, 10) ? 'Yesterday' : dayShort(ny)
  return `${label} ${t}`
}

export const TENDER: Record<string, string> = { cash: 'Cash', zelle: 'Zelle', crypto: 'Crypto', paypal: 'PayPal', card: 'Card', trade_credit: 'Trade-in credit' }
export const tender = (v: unknown) => TENDER[String(v ?? '')] ?? String(v ?? '')
export const CHANNEL: Record<string, string> = { counter: 'Counter', pos: 'POS', web: 'Website' }
export const channel = (v: unknown) => CHANNEL[String(v ?? '')] ?? String(v ?? '')
export const ROUTE: Record<string, string> = { consignify: 'Consignify', bank: 'Bank', crypto: 'Crypto', cash: 'Cash' }
export const ROUTE_LONG: Record<string, string> = { consignify: 'Through Consignify (card sales)', bank: 'From the bank (cash, Zelle, PayPal)', crypto: 'In crypto', cash: 'In cash' }
export const route = (v: unknown) => ROUTE[String(v ?? '')] ?? String(v ?? '')
export const METHOD: Record<string, string> = { zelle: 'Zelle', wire: 'Wire', cash: 'Cash', crypto: 'Crypto', consignify: 'Consignify payout', paypal: 'PayPal', check: 'Check' }
export const method = (v: unknown) => METHOD[String(v ?? '')] ?? String(v ?? '')

/** Consignor key → name, filled from the meta report (ReportsApp) so every screen names the person, not the number. */
let CONSIGNOR_NAMES: Record<string, string> = {}
export function setConsignorNames(m: Record<string, string> | null | undefined) { if (m) CONSIGNOR_NAMES = m }
/** A consignor added on this screen, named at once (the next meta read carries it too). */
export function addConsignorName(key: string, name: string) { CONSIGNOR_NAMES = { ...CONSIGNOR_NAMES, [key]: name } }
/** The consignor's name for a key; the key itself when the name is not known. */
export const consignorName = (key: unknown) => { const k = String(key ?? ''); return k ? CONSIGNOR_NAMES[k] || k : '' }

/** Whose piece, as a person reads it. */
export function owner(o: unknown, consignorKey?: unknown) {
  switch (String(o ?? '')) {
    case 'shane': return 'Shane'
    case 'masih': return 'Masih'
    case 'split': return 'Split'
    case 'consignor': return consignorKey ? consignorName(consignorKey) : 'Consignor'
    case 'unknown': case '': return 'Not recorded'
    default: return String(o)
  }
}
export const STATUS: Record<string, string> = { paid: 'Paid', awaiting_payment: 'Awaiting payment', awaiting_card: 'Awaiting card', cancelled: 'Cancelled', voided: 'Voided', recorded: 'Recorded', pending: 'Pending', due: 'Not yet paid', settled: 'Settled', na: '' }
export const status = (v: unknown) => STATUS[String(v ?? '')] ?? String(v ?? '')
export const INTAKE_TYPE: Record<string, string> = { bought: 'Bought', consigned: 'Consigned', traded: 'Traded in' }
export const intakeType = (v: unknown) => INTAKE_TYPE[String(v ?? '')] ?? String(v ?? '')

/** "Card 60% · Zelle 40%" from the allocation's tender list. */
export function tendersLabel(ts: { tender: string; pct: number; coin?: string | null }[] | undefined) {
  if (!ts?.length) return ''
  return ts.map((t) => `${tender(t.tender)}${t.coin ? ` (${t.coin})` : ''}${ts.length > 1 ? ` ${Math.round(t.pct)}%` : ''}`).join(' · ')
}

/** Data health: what each issue kind means and what to do. */
export const ISSUE: Record<string, { label: string; meaning: string; fix: string; to?: string }> = {
  line_no_cost: { label: 'Cost missing on sold piece', meaning: 'Shopify unit cost was blank at the moment of sale; profit is shown without it', fix: 'Enter the cost in Shopify; the nightly enrich picks it up', to: '/log' },
  line_no_owner: { label: 'Owner not recorded on sold piece', meaning: 'No ownership tag, Consignify record or intake row said whose piece it was', fix: 'Mark whose piece in QA; the nightly enrich picks it up', to: '/log' },
  sale_no_order: { label: 'Card sale never charged', meaning: 'A counter sale has waited for the card for more than a day; no Shopify order carries it', fix: 'Cancel it on the iPad or charge it in POS', to: '/fees' },
  order_not_in_ledger: { label: 'Shopify order not in the ledger', meaning: 'Shopify has a paid order since go-live that the ledger never booked', fix: 'The nightly orders sync books it; if it stays, check the webhook', to: '/fees' },
  amount_mismatch: { label: 'Order total differs from the counter sale', meaning: 'The Shopify order and the counter record disagree by more than a dollar', fix: 'Compare the two; a POS discount or an edit is the usual cause', to: '/fees' },
  day_not_closed: { label: 'Day not closed', meaning: 'Counter sales that day but nobody closed out on the iPad', fix: 'An owner reopens and closes the day on the iPad', to: '/daily' },
  drawer_over_short: { label: 'Drawer over or short by $20 or more', meaning: 'The counted cash was off from what the sales say', fix: 'Read the closer\'s note; adjust if it was a miscount', to: '/daily' },
  below_floor_no_approver: { label: 'Sold below floor without an approver', meaning: 'A piece went under its floor price and no owner ID signed it', fix: 'Ask who approved it; the record cannot be changed', to: '/log' },
  negative_margin: { label: 'Sold below cost', meaning: 'The price paid is less than the cost recorded at sale', fix: 'Check the cost in Shopify; a wrong cost is the usual cause', to: '/log' },
  barcode_sold_twice: { label: 'Same piece sold twice', meaning: 'Two paid sales carry the same variant and neither was refunded', fix: 'One of them is wrong: void or refund it', to: '/log' },
  refund_unmatched: { label: 'Refund without a matching line', meaning: 'A partial refund smaller than any line on the order (shipping, say)', fix: 'The accountant confirms what it was', to: '/fees' },
  payout_overdue: { label: 'Consignor balance over 30 days', meaning: 'Money owed on a sale older than 30 days is still unpaid', fix: 'Pay it and record the payout', to: '/payouts' },
  fee_estimated: { label: 'Card fee not in yet', meaning: 'Shopify has not reported the fee; an estimate is shown until it does', fix: 'Clears itself', to: '/fees' },
  listing_live_no_qa: { label: 'Listing live without a QA pass', meaning: 'Went live before Approve on the QA tab', fix: 'QA it on the iPad' },
  consignify_share_mismatch: { label: 'Consignify share mismatch', meaning: 'The app\'s tag and the Consignify share disagree; the settlement uses the tag', fix: 'Fix the share in Consignify' },
}
export function issueLabel(kind: unknown) { return ISSUE[String(kind)]?.label ?? String(kind).replace(/_/g, ' ') }

/** One short line saying where an issue sits, from its detail. */
export function issueWhere(i: Row): string {
  const d = (i.detail ?? {}) as Record<string, unknown>
  const parts: string[] = []
  if (d.sale_no) parts.push(`Sale #${d.sale_no}`)
  if (d.order) parts.push(`Order ${d.order}`)
  if (d.title) parts.push(String(d.title))
  if (d.payee) parts.push(String(d.payee))
  if (d.channel) parts.push(route(d.channel))
  if (d.price != null) parts.push(money(d.price))
  if (d.total != null) parts.push(money(d.total))
  if (d.balance != null) parts.push(money(d.balance))
  if (d.oldest_days != null) parts.push(`oldest ${d.oldest_days} days`)
  if (d.sales && Array.isArray(d.sales)) parts.push(`sales ${(d.sales as unknown[]).join(', ')}`)
  if (i.entity === 'day') parts.unshift(dayDow(i.entity_id))
  if (i.entity === 'product' && !d.title) parts.push(`Product ${i.entity_id}`)
  return parts.join(' · ') || String(i.entity_id ?? '')
}
export type Row = Record<string, unknown>
